/**
 * Procedural sound effects via Web Audio API.
 * No remote assets — all sounds are synthesised on the fly.
 * AudioContext is created lazily on the first play call (browser policy).
 */

const STORAGE_KEY_VOLUME = "neon-arena.sfx-volume";
const STORAGE_KEY_MUTED  = "neon-arena.sfx-muted";

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  private _volume: number;
  private _muted: boolean;

  // Per-sound cooldown tracking (ms timestamps of last play)
  private lastPlayed = new Map<string, number>();

  constructor() {
    this._volume = parseFloat(localStorage.getItem(STORAGE_KEY_VOLUME) ?? "0.5");
    this._muted  = localStorage.getItem(STORAGE_KEY_MUTED) === "true";
    if (!Number.isFinite(this._volume) || this._volume < 0 || this._volume > 1) {
      this._volume = 0.5;
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  get muted(): boolean { return this._muted; }
  get volume(): number { return this._volume; }

  /**
   * Returns the shared AudioContext and master GainNode, initialising them if
   * needed. Called by EngineAudio so both systems share one AudioContext.
   */
  ensureContext(): { ctx: AudioContext; master: GainNode } {
    this.getCtx();
    return { ctx: this.ctx!, master: this.master! };
  }

  setVolume(v: number): void {
    this._volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this._volume;
    localStorage.setItem(STORAGE_KEY_VOLUME, String(this._volume));
  }

  setMuted(m: boolean): void {
    this._muted = m;
    localStorage.setItem(STORAGE_KEY_MUTED, String(m));
    if (this.master) this.master.gain.value = m ? 0 : this._volume;
    if (!m) this.ctx?.resume();
  }

  /** Fire sound — throttled to weapon reload rate so held-fire doesn't spam. */
  playFire(reloadMs: number): void {
    if (!this.throttle("fire", reloadMs)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    // Short mid-freq thump + brief high noise burst
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.08);
    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    osc.connect(gain);
    gain.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.13);

    // Noise transient
    this.noiseBlast(ctx, t, 0.06, 2400, 0.18);
  }

  /** Hit/impact on a vehicle. */
  playHit(distanceSq: number): void {
    if (!this.throttle("hit", 80)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    const vol = this.distanceVol(distanceSq, 0.55);
    if (vol <= 0) return;
    this.noiseBlast(ctx, t, 0.18, 900, vol);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(160, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.15);
    g.gain.setValueAtTime(vol * 0.4, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.19);
  }

  /** Explosion — death or mine detonation. */
  playExplosion(distanceSq: number): void {
    if (!this.throttle("explode", 150)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    const vol = this.distanceVol(distanceSq, 0.85);
    if (vol <= 0) return;
    this.noiseBlast(ctx, t, 0.55, 400, vol);
    // Low boom
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(90, t);
    osc.frequency.exponentialRampToValueAtTime(28, t + 0.5);
    g.gain.setValueAtTime(vol * 0.7, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.56);
  }

  /** Played when the local player's vehicle is damaged. */
  playDamage(): void {
    if (!this.throttle("damage", 200)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    this.noiseBlast(ctx, t, 0.12, 1200, 0.5);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.1);
    g.gain.setValueAtTime(0.3, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.16);
  }

  /** Played when the local player dies. */
  playDeath(): void {
    if (!this.throttle("death", 2000)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    this.noiseBlast(ctx, t, 0.8, 300, 0.9);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(20, t + 0.8);
    g.gain.setValueAtTime(0.7, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.85);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.9);
  }

  /** Played when the local player respawns. */
  playRespawn(): void {
    if (!this.throttle("respawn", 2000)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    // Rising arpeggio
    const freqs = [220, 330, 440, 660];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      const st = t + i * 0.08;
      osc.frequency.setValueAtTime(f, st);
      g.gain.setValueAtTime(0.3, st);
      g.gain.exponentialRampToValueAtTime(0.001, st + 0.15);
      osc.connect(g);
      g.connect(this.master!);
      osc.start(st);
      osc.stop(st + 0.16);
    });
  }

  /** Played on boost activation. */
  playBoost(): void {
    if (!this.throttle("boost", 500)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.18);
    g.gain.setValueAtTime(0.22, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.23);
    this.noiseBlast(ctx, t, 0.1, 3000, 0.1);
  }

  /** Generic UI click. */
  playClick(): void {
    if (!this.throttle("click", 60)) return;
    const ctx = this.getCtx();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, t);
    g.gain.setValueAtTime(0.15, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
    osc.connect(g);
    g.connect(this.master!);
    osc.start(t);
    osc.stop(t + 0.05);
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private getCtx(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this._muted ? 0 : this._volume;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
    return this.ctx;
  }

  /** Returns true if enough time has passed since the last play of this sound. */
  private throttle(key: string, minGapMs: number): boolean {
    if (this._muted) return false;
    const now = performance.now();
    const last = this.lastPlayed.get(key) ?? 0;
    if (now - last < minGapMs) return false;
    this.lastPlayed.set(key, now);
    return true;
  }

  /**
   * Simple linear distance attenuation.
   * distanceSq — squared distance from listener to source.
   * maxVol     — volume at distance 0.
   * Fades linearly from 20 m (full) to 100 m (silent).
   */
  private distanceVol(distanceSq: number, maxVol: number): number {
    const NEAR = 20, FAR = 100;
    const dist = Math.sqrt(distanceSq);
    if (dist >= FAR) return 0;
    if (dist <= NEAR) return maxVol;
    return maxVol * (1 - (dist - NEAR) / (FAR - NEAR));
  }

  /** White noise burst through a bandpass filter. */
  private noiseBlast(ctx: AudioContext, t: number, dur: number, freq: number, vol: number): void {
    const bufSize = Math.ceil(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;

    const src = ctx.createBufferSource();
    src.buffer = buf;

    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = freq;
    filter.Q.value = 1.2;

    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);

    src.connect(filter);
    filter.connect(g);
    g.connect(this.master!);
    src.start(t);
    src.stop(t + dur + 0.01);
  }
}
